"use client";

import React from "react";
import Link from "next/link";

import { cn } from "./cn";

/**
 * Port of the reference's EmptyState (src/components/ui/EmptyState.tsx). No
 * reference test — EmptyState.test.tsx below is authored.
 */

interface EmptyStateAction {
  label: string;
  href?: string;
  onClick?: () => void;
  /** #620: disables the onClick variant while an async action (e.g. a
   *  mint-and-open) is in flight. Ignored for the href/Link variant —
   *  a navigation link has no pending state to guard. */
  disabled?: boolean;
}

interface EmptyStateProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  subtitle?: string;
  action?: EmptyStateAction;
}

export function EmptyState({
  icon: Icon,
  title,
  subtitle,
  action,
}: EmptyStateProps) {
  return (
    <div className="py-12 px-4 text-center card-enter">
      <div className="w-12 h-12 rounded-xl bg-elevated flex items-center justify-center mx-auto mb-4">
        <Icon size={24} className="text-ink-faint" />
      </div>

      <p className="text-base font-medium text-ink mt-2">{title}</p>

      {subtitle && (
        <p className="text-sm text-ink-light mt-1 max-w-sm mx-auto">
          {subtitle}
        </p>
      )}

      {action && <EmptyStateActionButton action={action} />}
    </div>
  );
}

function EmptyStateActionButton({ action }: { action: EmptyStateAction }) {
  const className =
    "mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors";

  if (action.href) {
    return (
      <Link href={action.href} className={className}>
        {action.label}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      className={cn(className, "disabled:opacity-60")}
    >
      {action.label}
    </button>
  );
}

export type { EmptyStateProps };
